<template>
  <!--suppress JSValidateTypes -->
  <md-preview v-if="props.previewOnly" :model-value="modelValue" :theme="$q.dark.isActive ? 'dark': 'light'" />
  <md-editor
    v-else
    :id="editorId"
    ref="mdEditorElement" v-model="modelValue"
    :toolbars="props.minimal ? minimalToolbars : toolbars"
    :footers="footers" language="fr-FR" no-prettier no-upload-img
    :theme="$q.dark.isActive ? 'dark': 'light'"
    :class="isAdmin || isTenantAdmin ? 'admin' : ''"
  >
    <!--suppress VueUnrecognizedSlot -->
    <template #defToolbars>
      <normal-toolbar title="Insérer une image" @on-click="showingImageUploadDialogRef = true">
        <template #trigger>
          <LucideImage class="md-editor-icon" />
          <image-upload-dialog v-model="showingImageUploadDialogRef" no-activator :public-file="publicContent" @uploaded="onUploaded" />
        </template>
      </normal-toolbar>
    </template>
    <!--suppress VueUnrecognizedSlot -->
    <template #defFooters>
      <div class="md-editor-footer-item" :class="modelValue.length > maxLength ? 'text-red text-weight-bold':''">
        <label class="md-editor-footer-label">
          Nombre de caractères:
        </label>
        <span>{{ modelValue.length }}/{{ maxLength }}</span>
      </div>
    </template>
  </md-editor>
</template>

<script setup>
  import { MdEditor, MdPreview, NormalToolbar, config as MdEditorConfig } from 'md-editor-v3'
  import { LucideImage } from 'lucide-vue-next'
  import mdEditorFr from '@vavt/md-editor-extension/dist/locale/fr-FR'
  import 'md-editor-v3/lib/style.css'
  import { computed, ref } from 'vue'
  import ImageUploadDialog from 'src/modules/documents/components/ImageUploadDialog'
  import { getStreamUrl } from 'src/modules/documents/helpers/fileHelper'
  import { isAdmin, isTenantAdmin } from 'src/services/session.js'
  import { useQuasar } from 'quasar'
  import { logger } from '@sentry/vue'

  const minimalToolbars = [
    'bold',
    'underline',
    'italic',
    '-',
    'strikeThrough',
    'title',
    'sub',
    'sup',
    'quote',
    'unorderedList',
    'orderedList',
    'task' // ^2.4.0
  ]

  const toolbars = [
    ...minimalToolbars,
    '-',
    'link',
    0,
    'table',
    'mermaid',
    'katex',
    '-',
    'revoke',
    'next',
    '=',
    'pageFullscreen',
    'preview',
    'htmlPreview'
  ]

  const footers = [
    0,
    '=',
    'scrollSwitch'
  ]

  const mdEditorElement = ref(null)

  MdEditorConfig({
    editorConfig: {
      languageUserDefined: {
        'fr-FR': mdEditorFr
      }
    },
    markdownItConfig(mi) {
      const defaultImageRenderer = mi.renderer.rules.image

      mi.renderer.rules.image = (tokens, idx, options, env, self) => {
        const token = tokens[idx]

        let src = token.attrGet('src') || ''
        if (src.startsWith('urn:aptc:file:')) {
          const parts = src.split(':')
          const last = parts[parts.length - 1] || ''
          try {
            src = getStreamUrl(last)
            token.attrSet('src', src)
          } catch (error) {
            logger.error(error)
            // we leave the URL as is if the transformation fails
          }
        }

        return defaultImageRenderer(tokens, idx, options, env, self)
      }
    }
  })

  const props = defineProps({
    editorId: {
      type: String,
      required: true
    },
    modelValue: String,
    maxLength: {
      type: Number,
      default: 10000
    },
    minimal: Boolean,
    // Mark the content of the editor as public. Image uploaded inside will be flagged as public
    publicContent: Boolean,
    previewOnly: Boolean
  })

  const emit = defineEmits(['update:modelValue', 'linkFile'])
  const modelValue = computed({
    get() {
      return props.modelValue || ''
    },
    set(value) {
      emit('update:modelValue', value)
    }
  })

  const showingImageUploadDialogRef = ref(false)

  const $q = useQuasar()

  async function onUploaded(file) {
    // const textareaDom = document.getElementById(props.editorId + '-textarea')
    const targetValue = `![${file.name}](urn:aptc:file:${file.mimeType}:${file.id})\n`

    mdEditorElement.value.insert(_selectedText => {
      return {
        targetValue,
        select: false
      }
    })

    emit('linkFile', file.id)
  }
</script>

<style lang="scss" scoped>
  // Barbarians style, waiting update on md-editor
  :deep(.md-editor-preview p) {
    word-break: break-word;
  }

  .md-editor-dark {
    background-color: #17405e !important;
    --md-editor-color: #e0e0e0;
    --md-color: white;
    --md-bk-color: #17405e;
    --md-border-color: #636060;
    --md-scrollbar-bg-color: #636060;
    --md-bk-color-outstand: #636060;
  }
</style>
